import {
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { DealPLOverrides, PlReconciliationAdjustment, ProjectPLBucket } from '../types';

const COLLECTION = 'dealPLOverrides';

function toDateOrUndef(value: any): Date | undefined {
  if (!value) return undefined;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : undefined;
}

function mapPlReconciliations(raw: unknown): PlReconciliationAdjustment[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const rows: PlReconciliationAdjustment[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const amount = Number(row.amount);
    const id = typeof row.id === 'string' ? row.id : '';
    const createdAt = toDateOrUndef(row.createdAt);
    if (!id || !Number.isFinite(amount) || !createdAt) continue;
    rows.push({
      id,
      amount: Math.round(amount),
      title: typeof row.title === 'string' ? row.title : '',
      reason: typeof row.reason === 'string' ? row.reason : '',
      source: row.source === 'crm' ? 'crm' : 'finance_agent',
      createdAt,
      createdBy: typeof row.createdBy === 'string' ? row.createdBy : '',
      idempotencyKey: typeof row.idempotencyKey === 'string' ? row.idempotencyKey : undefined,
      appliesToProgressBilling: row.appliesToProgressBilling === true ? true : undefined,
      voidedAt: toDateOrUndef(row.voidedAt),
      voidedBy: typeof row.voidedBy === 'string' ? row.voidedBy : undefined,
    });
  }
  return rows.length ? rows : undefined;
}

function fromFirestore(id: string, data: any): DealPLOverrides {
  return {
    id,
    dealId: data?.dealId || id,
    expenseBuckets: data?.expenseBuckets || {},
    notes: data?.notes,
    plReconciliations: mapPlReconciliations(data?.plReconciliations),
    excluded: Boolean(data?.excluded),
    excludedReason: data?.excludedReason,
    excludedAt: toDateOrUndef(data?.excludedAt),
    updatedAt: toDateOrUndef(data?.updatedAt),
    updatedBy: data?.updatedBy,
  };
}

export async function fetchDealPLOverrides(dealId: string): Promise<DealPLOverrides | null> {
  if (!dealId) return null;
  const snap = await getDoc(doc(db, COLLECTION, dealId));
  if (!snap.exists()) return null;
  return fromFirestore(snap.id, snap.data());
}

/**
 * Subscribe to a single deal's P&L overrides.
 *
 * Returns the unsubscribe function. The callback is called with `null` until
 * the doc exists (which is normal — overrides are written lazily by PMs).
 */
export function subscribeDealPLOverrides(
  dealId: string,
  onChange: (overrides: DealPLOverrides | null) => void,
): Unsubscribe {
  if (!dealId) {
    onChange(null);
    return () => undefined;
  }
  return onSnapshot(doc(db, COLLECTION, dealId), snap => {
    if (!snap.exists()) {
      onChange(null);
      return;
    }
    onChange(fromFirestore(snap.id, snap.data()));
  });
}

/**
 * Subscribe to several deals' P&L overrides as a single keyed map. Wraps N
 * `onSnapshot` listeners; cheap for the project P&L view (rarely > 50 active).
 */
export function subscribeDealPLOverridesMap(
  dealIds: string[],
  onChange: (map: Record<string, DealPLOverrides>) => void,
): Unsubscribe {
  const unique = Array.from(new Set(dealIds.filter(Boolean)));
  const cache: Record<string, DealPLOverrides> = {};
  const unsubs: Unsubscribe[] = unique.map(id =>
    onSnapshot(doc(db, COLLECTION, id), snap => {
      if (snap.exists()) {
        cache[id] = fromFirestore(snap.id, snap.data());
      } else {
        delete cache[id];
      }
      onChange({ ...cache });
    }),
  );
  // Emit initial empty map so consumers get a deterministic first value.
  onChange({ ...cache });
  return () => {
    unsubs.forEach(u => {
      try {
        u();
      } catch {
        /* ignore */
      }
    });
  };
}

interface UpsertContext {
  uid?: string;
}

interface OverridePatch {
  notes?: string;
  /** `null` clears an existing bucket override for that expense. */
  expenseBuckets?: Record<string, ProjectPLBucket | null>;
}

async function upsert(
  dealId: string,
  patch: OverridePatch,
  ctx: UpsertContext = {},
): Promise<void> {
  if (!dealId) return;
  const ref = doc(db, COLLECTION, dealId);
  const payload: Record<string, any> = {
    dealId,
    updatedAt: serverTimestamp(),
  };
  if (ctx.uid) payload.updatedBy = ctx.uid;
  if (patch.notes !== undefined) payload.notes = patch.notes;
  if (patch.expenseBuckets) {
    // setDoc({ merge: true }) deep-merges nested objects key-by-key, so writing
    // a partial `expenseBuckets` object preserves siblings. Dotted-path field
    // names only work with updateDoc; here they'd be stored as literal keys.
    const nested: Record<string, any> = {};
    for (const [expenseId, bucket] of Object.entries(patch.expenseBuckets)) {
      if (!expenseId) continue;
      // `null` means "clear override" — use deleteField so the doc stays clean.
      nested[expenseId] = bucket === null ? deleteField() : bucket;
    }
    if (Object.keys(nested).length > 0) {
      payload.expenseBuckets = nested;
    }
  }
  await setDoc(ref, payload, { merge: true });
}

/** Set or clear a single QB expense → bucket override for a deal. */
export async function setExpenseBucketOverride(
  dealId: string,
  expenseId: string,
  bucket: ProjectPLBucket | null,
  ctx: UpsertContext = {},
): Promise<void> {
  if (!dealId || !expenseId) return;
  await upsert(dealId, { expenseBuckets: { [expenseId]: bucket } }, ctx);
}

/** Update the optional PM-facing note on the project row. */
export async function setDealPLNotes(
  dealId: string,
  notes: string,
  ctx: UpsertContext = {},
): Promise<void> {
  await upsert(dealId, { notes }, ctx);
}

/**
 * Toggle whether a project counts towards Project P&L KPIs / default table.
 *
 * Excluded projects:
 *   - drop out of the active table by default (a "Excluded" filter still surfaces them),
 *   - skip overhead allocation so the remaining active projects share the pool fairly,
 *   - are ignored by the KPI strip (revenue, cost, margin, variance).
 *
 * Pass `false` to re-include. When excluding, an optional reason is stored
 * for the audit trail (shown next to the row chip).
 */
export async function setDealPLExcluded(
  dealId: string,
  excluded: boolean,
  ctx: UpsertContext & { reason?: string } = {},
): Promise<void> {
  if (!dealId) return;
  const ref = doc(db, COLLECTION, dealId);
  const payload: Record<string, any> = {
    dealId,
    excluded,
    updatedAt: serverTimestamp(),
  };
  if (ctx.uid) payload.updatedBy = ctx.uid;
  if (excluded) {
    payload.excludedAt = serverTimestamp();
    payload.excludedReason = ctx.reason ?? '';
  } else {
    // Clearing the flag should also clear its metadata so the audit trail
    // doesn't leave a stale "excluded since…" date hanging around.
    payload.excludedAt = deleteField();
    payload.excludedReason = deleteField();
  }
  await setDoc(ref, payload, { merge: true });
}

const MIN_RECONCILIATION_REASON = 10;

/**
 * Append a P&L-only revenue adjustment. Does not change the deal value or
 * create a change order. Prefer the finance agent `patchProjectPlReconciliation`
 * action for agent writes; this is the CRM path.
 */
export async function setDealPLReconciliation(
  dealId: string,
  input: { amount: number; title: string; reason: string },
  ctx: UpsertContext = {},
): Promise<void> {
  if (!dealId) return;
  const title = input.title.trim();
  const reason = input.reason.trim();
  const amount = Math.round(Number(input.amount));
  if (!title) throw new Error('title is required');
  if (reason.length < MIN_RECONCILIATION_REASON) {
    throw new Error(`reason must be at least ${MIN_RECONCILIATION_REASON} characters`);
  }
  if (!Number.isFinite(amount) || amount === 0) throw new Error('amount must be a non-zero number of dollars');

  const ref = doc(db, COLLECTION, dealId);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data()?.plReconciliations : undefined;
  const list = Array.isArray(existing) ? [...existing] : [];
  list.push({
    id: `plr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    amount,
    title,
    reason,
    source: 'crm',
    createdAt: Timestamp.now(),
    createdBy: ctx.uid || 'crm',
  });
  await setDoc(ref, {
    dealId,
    plReconciliations: list,
    updatedAt: serverTimestamp(),
    ...(ctx.uid ? { updatedBy: ctx.uid } : {}),
  }, { merge: true });
}

/** Convenience for tests / migrations. */
export function dealPLOverridesTimestamp(date: Date): Timestamp {
  return Timestamp.fromDate(date);
}
